import { useState } from 'react';

import { CopyIcon, EllipsisVerticalIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { DownloadConversationItem } from './DownloadConversationItem';
import { useCreateSimulationLink } from '../../hooks/useCreateSimulationLink';
import { useDownloadConversation } from '../../hooks/useDownloadConversation';
import { selectIsReplay } from '../../redux/selectors';

type Props = { sessionId: string };

export const ChatActionsMenu = ({ sessionId }: Props) => {
  const { t } = useTranslation();
  const isReplay = useSelector(selectIsReplay);
  const simulation = useCreateSimulationLink();
  const [isOpen, setIsOpen] = useState(false);
  const download = useDownloadConversation(sessionId, () => setIsOpen(false));

  const copyConversationId = () => {
    navigator.clipboard.writeText(sessionId);
    toast.success(t('common.copied'));
  };

  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost-neutral" size="icon-sm" aria-label={t('try_it.more_actions')}>
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="z-3000">
        <DropdownMenuItem onSelect={copyConversationId}>
          <CopyIcon size={16} className="text-foreground-muted" />
          {t('try_it.copy_conversation_id')}
        </DropdownMenuItem>

        {isReplay && <DownloadConversationItem download={download} />}

        {simulation && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a
                href={simulation.href}
                target="_blank"
                rel="noopener"
                onPointerDown={simulation.stash}
                onClick={simulation.stash}
                aria-label={t('try_it.create_simulation_new_tab')}
              >
                <PlusIcon size={16} className="text-foreground-muted" />
                {t('try_it.create_simulation')}…
              </a>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
